import "../styles/globals.css";
import "../utils/bugsnag";

import { useEffect } from "react";
import { NextPageContext } from "next";
import { AppProps } from "next/app";
import Router from "next/router";
import { ApolloProvider, ApolloClient } from "@apollo/client";
import { getDataFromTree } from "@apollo/client/react/ssr";
import withApollo from "@paperlesspost/next-with-apollo";

import { AuthProvider } from "../contexts/Auth/context";
import GtmScript from "../components/GtmScript";
import Layout from "../components/Layout";
import ErrorBoundary from "../components/ErrorBoundary";
import getApolloClient from "../utils/getApolloClient";

import { BrowserSizeProvider } from "../hooks/useBrowserSize";
import { ServerContextProvider } from "../hooks/useServerContext";
import fastpanel, { configureFastpanel } from "../utils/fastpanel";

type Props = AppProps & {
  apollo: ApolloClient<{}>;
  userAgent: string;
  serverContext?: {
    req: NextPageContext["req"];
    res: NextPageContext["res"];
  };
};

let prevURLBase: string;
Router.events.on("routeChangeComplete", (url: string) => {
  const urlBase = url.split("?")[0];

  if (typeof prevURLBase === "undefined") {
    prevURLBase = urlBase;
    return;
  }

  if (urlBase !== prevURLBase) {
    prevURLBase = urlBase;
    fastpanel({ name: "pbr_page_load" });
  }
});

const App = (props: Props) => {
  const { Component, pageProps, apollo, userAgent, serverContext } = props;

  useEffect(() => {
    const fastpanelConfiguredPromise = configureFastpanel();

    // Run the iniitial page load tracking call.
    fastpanel({ name: "pbr_page_load", initialLoad: true });
    // Set up the base URL for future tracking calls.  Static for linting purposes.
    prevURLBase = Router.asPath.split("?")[0];
  }, []);

  return (
    <ErrorBoundary>
      <ApolloProvider client={apollo}>
        <ServerContextProvider serverContext={serverContext}>
          <BrowserSizeProvider userAgent={userAgent}>
            <AuthProvider>
              <Layout>
                <Component {...pageProps} />
              </Layout>
              <GtmScript />
            </AuthProvider>
          </BrowserSizeProvider>
        </ServerContextProvider>
      </ApolloProvider>
    </ErrorBoundary>
  );
};

App.getInitialProps = ({ ctx }: any) => {
  const { req, res } = ctx;
  const userAgent = req ? req.headers["user-agent"] : navigator.userAgent;
  const serverContext = {
    req,
    res,
    // This prevents the server attempting to serialize the server context
    // object to JSON for hydration on the client
    toJSON: () => undefined,
  };
  return { serverContext, userAgent };
};

// don't cache the page if any of the graphql requests fail.
const onError = (_error: any, ctx?: NextPageContext) => {
  ctx?.res?.setHeader("Surrogate-Control", "private, no-store");
};

export default withApollo(getApolloClient, { getDataFromTree, onError })(App);
